import React, { useEffect, useState, useRef } from 'react';
import { Search, X, ChevronDown, Bug, BookOpen, Zap, Layers, Filter, SlidersHorizontal } from 'lucide-react';
import { Issue } from '../../data/mockData';
export interface BoardFilterState {
  search: string;
  assignees: string[];
  priorities: ('High' | 'Medium' | 'Low')[];
  types: ('Story' | 'Bug' | 'Task' | 'Epic')[];
}
export const defaultFilters: BoardFilterState = {
  search: '',
  assignees: [],
  priorities: [],
  types: []
};
interface BoardFiltersProps {
  issues: Issue[];
  filters: BoardFilterState;
  onFiltersChange: (filters: BoardFilterState) => void;
  filteredCount: number;
}
const AVATAR_COLORS = ['bg-indigo-900/50 border-indigo-800 text-indigo-300', 'bg-emerald-900/50 border-emerald-800 text-emerald-300', 'bg-amber-900/50 border-amber-800 text-amber-300', 'bg-rose-900/50 border-rose-800 text-rose-300', 'bg-cyan-900/50 border-cyan-800 text-cyan-300', 'bg-violet-900/50 border-violet-800 text-violet-300', 'bg-orange-900/50 border-orange-800 text-orange-300', 'bg-pink-900/50 border-pink-800 text-pink-300'];
function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}
export function BoardFilters({
  issues,
  filters,
  onFiltersChange,
  filteredCount
}: BoardFiltersProps) {
  const [isOpen, setIsOpen] = useState(true);
  const [isAssigneeDropdownOpen, setIsAssigneeDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Extract unique assignees
  const uniqueAssignees = Array.from(new Map(issues.filter(i => i.assignee).map(i => [i.assignee!.name, i.assignee!])).values()).sort((a, b) => a.name.localeCompare(b.name));
  const hasActiveFilters = filters.search !== '' || filters.assignees.length > 0 || filters.priorities.length > 0 || filters.types.length > 0;
  // Click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsAssigneeDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const togglePriority = (p: 'High' | 'Medium' | 'Low') => {
    const next = filters.priorities.includes(p) ? filters.priorities.filter(x => x !== p) : [...filters.priorities, p];
    onFiltersChange({
      ...filters,
      priorities: next
    });
  };
  const toggleType = (t: 'Story' | 'Bug' | 'Task' | 'Epic') => {
    const next = filters.types.includes(t) ? filters.types.filter(x => x !== t) : [...filters.types, t];
    onFiltersChange({
      ...filters,
      types: next
    });
  };
  const toggleAssignee = (name: string) => {
    const next = filters.assignees.includes(name) ? filters.assignees.filter(x => x !== name) : [...filters.assignees, name];
    onFiltersChange({
      ...filters,
      assignees: next
    });
  };
  const clearFilters = () => {
    onFiltersChange(defaultFilters);
  };
  if (!isOpen) {
    return <div className="px-8 py-2 border-b border-slate-800 bg-slate-900/30 flex items-center shrink-0" data-id="element-1826">
        <button onClick={() => setIsOpen(true)} className="flex items-center gap-2 text-xs text-slate-400 hover:text-slate-200 transition-colors px-2 py-1 rounded hover:bg-slate-800" data-id="element-1827">
          <Filter size={14} data-id="element-1828" />
          <span data-id="element-1829">Mostrar filtros</span>
          {hasActiveFilters && <span className="w-2 h-2 rounded-full bg-indigo-500 ml-1" data-id="element-1830" />}
        </button>
      </div>;
  }
  return <div className="px-8 py-3 border-b border-slate-800 bg-slate-900/60 flex flex-col gap-3 shrink-0 animate-in slide-in-from-top-2 duration-200" data-id="element-1831">
      <div className="flex items-center justify-between" data-id="element-1832">
        <div className="flex items-center gap-4 flex-wrap flex-1" data-id="element-1833">
          {/* Search */}
          <div className="relative w-64" data-id="element-1834">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" data-id="element-1835" />

            <input type="text" placeholder="Buscar issues..." value={filters.search} onChange={e => onFiltersChange({
            ...filters,
            search: e.target.value
          })} className="w-full bg-slate-950 border border-slate-800 rounded-lg pl-9 pr-3 py-1.5 text-xs text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/20 transition-all" data-id="element-1836" />

            {filters.search && <button onClick={() => onFiltersChange({
            ...filters,
            search: ''
          })} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300" data-id="element-1837">
                <X size={12} data-id="element-1838" />
              </button>}
          </div>

          <div className="w-px h-5 bg-slate-800" data-id="element-1839" />

          {/* Assignee Dropdown */}
          <div className="relative" ref={dropdownRef} data-id="element-1840">
            <button onClick={() => setIsAssigneeDropdownOpen(!isAssigneeDropdownOpen)} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs border transition-colors ${filters.assignees.length > 0 ? 'bg-indigo-950/30 border-indigo-500/30 text-indigo-300' : 'bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-700 hover:text-slate-200'}`} data-id="element-1841">
              <span data-id="element-1842">Assignee</span>
              {filters.assignees.length > 0 && <span className="bg-indigo-500/20 text-indigo-300 px-1.5 py-0.5 rounded text-[10px] font-bold" data-id="element-1843">
                  {filters.assignees.length}
                </span>}
              <ChevronDown size={12} className="opacity-70" data-id="element-1844" />
            </button>

            {isAssigneeDropdownOpen && <div className="absolute top-full left-0 mt-1 w-56 bg-slate-900 border border-slate-700 rounded-xl shadow-xl z-50 py-1 overflow-hidden" data-id="element-1845">
                <div className="max-h-64 overflow-y-auto p-1" data-id="element-1846">
                  {uniqueAssignees.length === 0 ? <div className="px-3 py-2 text-xs text-slate-500 text-center" data-id="element-1847">
                      No hay usuarios
                    </div> : uniqueAssignees.map(user => {
                const isSelected = filters.assignees.includes(user.name);
                return <button key={user.name} onClick={() => toggleAssignee(user.name)} className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-slate-800 rounded-lg text-left transition-colors" data-id="element-1848">
                          <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${isSelected ? 'bg-indigo-500 border-indigo-500 text-white' : 'border-slate-600 bg-slate-950'}`} data-id="element-1849">
                            {isSelected && <X size={10} className="rotate-45" style={{
                      transform: 'rotate(0deg)'
                    }} data-id="element-1850" />}
                          </div>
                          <div className={`w-5 h-5 rounded-full border flex items-center justify-center text-[8px] font-bold shrink-0 ${getAvatarColor(user.name)}`} data-id="element-1851">
                            {user.initials}
                          </div>
                          <span className="text-xs text-slate-300 truncate" data-id="element-1852">
                            {user.name}
                          </span>
                        </button>;
              })}
                </div>
              </div>}
          </div>

          <div className="w-px h-5 bg-slate-800" data-id="element-1853" />

          {/* Priority Toggles */}
          <div className="flex items-center gap-1 bg-slate-950 p-0.5 rounded-lg border border-slate-800" data-id="element-1854">
            {(['High', 'Medium', 'Low'] as const).map(p => {
            const isSelected = filters.priorities.includes(p);
            const colorClass = p === 'High' ? 'text-red-400 bg-red-400/10' : p === 'Medium' ? 'text-amber-400 bg-amber-400/10' : 'text-blue-400 bg-blue-400/10';
            return <button key={p} onClick={() => togglePriority(p)} className={`px-2.5 py-1 rounded-md text-[10px] font-medium transition-all ${isSelected ? colorClass : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800'}`} data-id="element-1855">
                  {p}
                </button>;
          })}
          </div>

          <div className="w-px h-5 bg-slate-800" data-id="element-1856" />

          {/* Type Toggles */}
          <div className="flex items-center gap-1 bg-slate-950 p-0.5 rounded-lg border border-slate-800" data-id="element-1857">
            {([{
            id: 'Story',
            icon: BookOpen,
            color: 'text-emerald-400'
          }, {
            id: 'Bug',
            icon: Bug,
            color: 'text-red-400'
          }, {
            id: 'Task',
            icon: Zap,
            color: 'text-blue-400'
          }, {
            id: 'Epic',
            icon: Layers,
            color: 'text-violet-400'
          }] as const).map(t => {
            const isSelected = filters.types.includes(t.id);
            const Icon = t.icon;
            return <button key={t.id} onClick={() => toggleType(t.id)} title={t.id} className={`p-1.5 rounded-md transition-all ${isSelected ? `bg-slate-800 ${t.color}` : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800'}`} data-id="element-1858">
                  <Icon size={12} data-id="element-1859" />
                </button>;
          })}
          </div>

          {/* Clear Filters */}
          {hasActiveFilters && <button onClick={clearFilters} className="flex items-center gap-1.5 px-2 py-1 text-[10px] text-slate-400 hover:text-slate-200 transition-colors ml-2" data-id="element-1860">
              <X size={12} data-id="element-1861" />
              Limpiar
            </button>}
        </div>

        {/* Right side: Count & Toggle */}
        <div className="flex items-center gap-4 shrink-0" data-id="element-1862">
          <span className="text-[10px] text-slate-500 font-medium" data-id="element-1863">
            Mostrando {filteredCount} de {issues.length}
          </span>
          <button onClick={() => setIsOpen(false)} className="p-1.5 text-slate-500 hover:text-slate-300 hover:bg-slate-800 rounded-md transition-colors" title="Ocultar filtros" data-id="element-1864">
            <SlidersHorizontal size={14} data-id="element-1865" />
          </button>
        </div>
      </div>
    </div>;
}